/**
 * Fetch remote images as data URLs for the PDF renderer.
 *
 * `@react-pdf/renderer`'s <Image> can take a URL directly, but doing that
 * here would hand the browser's own fetch to a URL the service worker has
 * very likely already cached as an opaque (status 0) response — Storage
 * downloads and OSM tiles are both CacheFirst in workbox-config.js,
 * including opaque responses (`cacheableResponse: { statuses: [0, 200] }`).
 * A `cors` fetch of a URL the SW already holds as opaque comes back with an
 * empty body, silently, with no error to catch. Appending a query param the
 * cached entry never carried forces a fresh cache key, so this fetch always
 * reaches the network (or a proper non-opaque cache entry of its own).
 */
const CACHE_BUSTER_PARAM = 'rpdf';
const CACHE_BUSTER_VALUE = '1';
const DEFAULT_CONCURRENCY = 6;

function withCacheBuster(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${CACHE_BUSTER_PARAM}=${CACHE_BUSTER_VALUE}`;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/** Null on any failure (network error, non-2xx, opaque/empty body) — a
 *  missing figure should never fail the whole download. */
export async function fetchAsDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(withCacheBuster(url), { mode: 'cors', credentials: 'omit' });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (blob.size === 0) return null;
    return await blobToDataUrl(blob);
  } catch {
    return null;
  }
}

export interface PrefetchResult {
  /** url -> data URL, only for URLs that resolved. */
  map: Map<string, string>;
  failedCount: number;
}

/**
 * Fetch every URL to a data URL, bounded concurrency, tolerant of partial
 * failure. Duplicate URLs (the same figure referenced twice, or a repeated
 * map tile) are fetched once.
 */
export async function prefetchImages(
  urls: string[],
  concurrency = DEFAULT_CONCURRENCY
): Promise<PrefetchResult> {
  const unique = [...new Set(urls)];
  const map = new Map<string, string>();
  let failedCount = 0;

  let cursor = 0;
  async function worker() {
    while (cursor < unique.length) {
      const index = cursor;
      cursor += 1;
      const url = unique[index];
      const result = await fetchAsDataUrl(url);
      if (result) map.set(url, result);
      else failedCount += 1;
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, unique.length) }, () => worker());
  await Promise.allSettled(workers);

  return { map, failedCount };
}
